/** Jobs group (canonical `H6IHDfRP`). The jobs hub triage list + the whole
 *  detail payload in one call, plus the small writes (status, create) and the
 *  existing-client picker. Goes through the shared client (Brief 01 §2). */

import { api } from "./client";
import type {
  JobsListResponse,
  JobListFilters,
  JobListRow,
  JobDetailResponse,
  JobCreateInput,
  JobCreateResponse,
  JobUpdateStatusResponse,
  JobAddressUpdateInput,
  JobAddressUpdateResponse,
  JobResumeResponse,
  JobClientPickerItem,
  ClientSearchResponse,
  ClientSearchResult,
  ClientAttachInput,
  ClientAttachResponse,
  ClientCreateAttachInput,
  ClientCreateAttachResponse,
  ClientSetLeadResponse,
  ClientUpdateInput,
  ClientUpdateResponse,
  ClientDetachResponse,
  DrawingSetCreateInput,
  DrawingUpdateInput,
  CompleteDrawingSetResponse,
  DrawingStatus,
  WorkstreamType,
  JobMergePreview,
  JobMergeResult,
  OrderBookResponse,
  ConstraintsReport,
} from "@/types";

/** GET /list_v2 — jobs triage list, the only Jobs list endpoint. (The legacy
 *  /list it replaced was retired 2026-08-31: it lacked exclude_archived /
 *  exclude_on_hold and still over-reported credit-settled invoices as
 *  outstanding — its XanoScript is archived at docs/legacy/jobs-list-api87.xs.)
 *  Booleans are only sent when true (the client drops undefined params), so an
 *  unchecked toggle never narrows the query. */
export function listJobs(filters: JobListFilters = {}): Promise<JobsListResponse> {
  return api.get<JobsListResponse>("jobs", "list_v2", {
    query: {
      page: filters.page,
      per_page: filters.per_page,
      sort_field: filters.sort_field,
      sort_direction: filters.sort_direction,
      q: filters.q,
      stages: filters.stages,
      only_open: filters.only_open || undefined,
      exclude_archived: filters.exclude_archived || undefined,
      exclude_on_hold: filters.exclude_on_hold || undefined,
      deadline_window: filters.deadline_window,
      has_overdue: filters.has_overdue || undefined,
      has_open_tasks: filters.has_open_tasks || undefined,
      has_unpaid: filters.has_unpaid || undefined,
      not_fully_invoiced: filters.not_fully_invoiced || undefined,
    },
  });
}

/** GET /detail?id= — the whole hub for one job (404 if not found). */
export function getJobDetail(id: number): Promise<JobDetailResponse> {
  return api.get<JobDetailResponse>("jobs", "detail", { query: { id } });
}

/** GET /list_v2?stages=completed — every completed job with its billing rollups,
 *  paginated to the end. Drives the Archive workbench, which decides archive-
 *  readiness client-side from each row's fee/invoice sums (no new endpoint:
 *  list_v2 already carries total_agreed / total_invoiced / remaining_to_invoice /
 *  total_outstanding / is_fully_invoiced + the unpaid/overdue flags). `stages` +
 *  a real-column sort keeps the fast path (q/has_unpaid would force a full scan).
 *  The page loop is capped so a contract change can't spin it forever. */
export async function listCompletedJobs(): Promise<JobListRow[]> {
  const PER_PAGE = 100;
  const out: JobListRow[] = [];
  for (let page = 1; page <= 50; page += 1) {
    const res = await listJobs({
      stages: "completed",
      per_page: PER_PAGE,
      page,
      sort_field: "job_number",
      sort_direction: "asc",
    });
    out.push(...res.items);
    if (!res.paging.has_more || res.items.length === 0) break;
  }
  return out;
}

/** GET /list_v2?stages=on_hold — every on-hold job with its billing rollups,
 *  paginated to the end. Drives the Jobs › On Hold page, which lists held jobs
 *  and resumes them one at a time. Same fast-path shape as listCompletedJobs
 *  (stages + a real-column sort), page loop capped so a contract change can't
 *  spin it forever. */
export async function listOnHoldJobs(): Promise<JobListRow[]> {
  const PER_PAGE = 100;
  const out: JobListRow[] = [];
  for (let page = 1; page <= 50; page += 1) {
    const res = await listJobs({
      stages: "on_hold",
      per_page: PER_PAGE,
      page,
      sort_field: "job_number",
      sort_direction: "asc",
    });
    out.push(...res.items);
    if (!res.paging.has_more || res.items.length === 0) break;
  }
  return out;
}

/** GET /list_v2?stages=active — the auto-created "shell" jobs (job_number 0, off-vocab
 *  status="active") that the Review › Jobs cleanup merges into real jobs. */
export function listShellJobs(): Promise<JobsListResponse> {
  return api.get<JobsListResponse>("jobs", "list_v2", { query: { stages: "active", per_page: 200 } });
}

/** POST /merge with dry_run — the no-write preview of a job merge (winner, what
 *  repoints, what backfills). Gate the real merge behind it. */
export function previewJobMerge(winnerJobId: number, loserJobId: number): Promise<JobMergePreview> {
  return api.post<JobMergePreview>("jobs", "merge", {
    body: { winner_job_id: winnerJobId, loser_job_id: loserJobId, dry_run: true },
  });
}

/** POST /merge — collapses the loser (shell) job into the winner (real) job:
 *  repoints its records, backfills the winner, tombstones the loser. Destructive;
 *  gate behind the dry-run preview + confirm. Re-fetch the shell list after. */
export function mergeJobs(winnerJobId: number, loserJobId: number): Promise<JobMergeResult> {
  return api.post<JobMergeResult>("jobs", "merge", {
    body: { winner_job_id: winnerJobId, loser_job_id: loserJobId },
  });
}

/** PATCH /update_status — server validates against the canonical set (400 else).
 *  Returns { id, status, previous_status }. Re-fetch /detail after. The only
 *  manual stages worth sending now are `on_hold` / `archived` (the rest are
 *  derived and overwritten on the next recompute). */
export function updateJobStatus(id: number, status: string): Promise<JobUpdateStatusResponse> {
  return api.patch<JobUpdateStatusResponse>("jobs", "update_status", { body: { id, status } });
}

/** PATCH /update_address — writes the structured site address (+ uprn/lat/lng)
 *  onto the job from an Ideal Postcodes selection. The Job Detail "Get Detailed
 *  Address" drawer's Save. A full overwrite of the address block (every field is
 *  written), so always send the complete set. Re-fetch /detail after. */
export function updateJobAddress(input: JobAddressUpdateInput): Promise<JobAddressUpdateResponse> {
  return api.patch<JobAddressUpdateResponse>("jobs", "update_address", { body: input });
}

/** POST /resume_job — un-hold / un-archive. Re-derives the stage from the job's
 *  current scope (e.g. a held job mid-drawings resumes to `in_progress`); the
 *  client must not guess the target. Returns { job } with the new status. */
export function resumeJob(jobId: number): Promise<JobResumeResponse> {
  return api.post<JobResumeResponse>("jobs", "resume_job", { body: { job_id: jobId } });
}

/** POST /create — New Job. Lazily creates a Xero contact for new clients
 *  (non-fatal); surface `xero_synced:false` softly. */
export function createJob(input: JobCreateInput): Promise<JobCreateResponse> {
  return api.post<JobCreateResponse>("jobs", "create", { body: input });
}

/** GET /clients?q= — lean existing-client picker (includes stub clients by
 *  design, so users attach rather than duplicate). The live endpoint wraps the
 *  rows in a Xano `{ items }` envelope (the brief's §2.7 bare-array shape was
 *  wrong); unwrap it, tolerating either shape. */
export async function listJobClients(q = ""): Promise<JobClientPickerItem[]> {
  const res = await api.get<JobClientPickerItem[] | { items: JobClientPickerItem[] }>(
    "jobs",
    "clients",
    { query: { q } },
  );
  return Array.isArray(res) ? res : res?.items ?? [];
}

/* ─── Clients tab (Brief 02) ───────────────────────────────────────────────
 * The tab reads from the /detail clients[] payload; these are the writes. After
 * each, the caller re-fetches /detail (the single-lead invariant + counts are
 * server-owned). */

/** GET /client_search — richer, stub-inclusive picker for the Add-client flow
 *  (q < 2 chars → empty). Unwraps the { results } envelope, tolerating a bare
 *  array. */
export async function searchClients(q = "", limit = 10): Promise<ClientSearchResult[]> {
  const res = await api.get<ClientSearchResponse | ClientSearchResult[]>("jobs", "client_search", {
    query: { q, limit },
  });
  return Array.isArray(res) ? res : res?.results ?? [];
}

/** POST /client_attach — link an existing client (idempotent). */
export function attachClient(input: ClientAttachInput): Promise<ClientAttachResponse> {
  return api.post<ClientAttachResponse>("jobs", "client_attach", { body: input });
}

/** POST /client_create_and_attach — create a client + link it (no Xero push). */
export function createAndAttachClient(input: ClientCreateAttachInput): Promise<ClientCreateAttachResponse> {
  return api.post<ClientCreateAttachResponse>("jobs", "client_create_and_attach", { body: input });
}

/** POST /client_set_lead — promote a linked client to sole lead. */
export function setLeadClient(jobId: number, clientId: number): Promise<ClientSetLeadResponse> {
  return api.post<ClientSetLeadResponse>("jobs", "client_set_lead", { body: { job_id: jobId, client_id: clientId } });
}

/** PATCH /clients/{id} — partial edit via the canonical Xero-aware client patch
 *  (API #255, shared with the Make.com Xero↔Xano sync). `id` goes in the PATH;
 *  send `is_company` as a boolean and the SPLIT address fields (not `address`).
 *  We tag `source:"app"` so a Xero-relevant change is pushed back to Xero (the
 *  Make Xero→Xano sync sends `source:"xero"` to suppress that and avoid a loop).
 *  Only sent fields change; a field sent as "" is CLEARED, an omitted field is
 *  left untouched. */
export function updateClient(input: ClientUpdateInput): Promise<ClientUpdateResponse> {
  const { id, ...fields } = input;
  return api.patch<ClientUpdateResponse>("jobs", `clients/${id}`, {
    body: { ...fields, source: "app" },
  });
}

/** POST /client_detach — remove the job_client LINK only (never the record). */
export function detachClient(jobId: number, clientId: number): Promise<ClientDetachResponse> {
  return api.post<ClientDetachResponse>("jobs", "client_detach", { body: { job_id: jobId, client_id: clientId } });
}

/* ─── Drawing-sets model (Addendum 2) ──────────────────────────────────────
 * Thin writes — the backend recompute routine is the sole writer of every
 * derived status and the auto-tasks. Every endpoint recomputes; after each the
 * caller re-reads /detail (nested drawing_sets[], mirrored rollups, spawned/
 * closed tasks). Bodies we don't consume are typed `unknown`. */

/** POST /create_drawing_set — seeds one drawing per `seed_types` (planning/other
 *  also get a draw task; BC gets none). **400 "Finish the current drawing set
 *  first"** if the current set still has incomplete drawings — gate the button. */
export function createDrawingSet(jobId: number, input: DrawingSetCreateInput): Promise<unknown> {
  return api.post<unknown>("jobs", "create_drawing_set", {
    body: {
      job_id: jobId,
      label: input.label || undefined,
      seed_types: input.seed_types?.length ? input.seed_types.join(",") : undefined,
    },
  });
}

/** POST /add_drawing — add one drawing to the CURRENT set (400 otherwise).
 *  planning/other spawn a draw task; BC does not (use createBcTask). Returns the
 *  new drawing row (id is used to back-fill an inherited title — see addDrawing
 *  in DrawingSetsTab). */
export function addDrawing(
  drawingSetId: number,
  drawingType: WorkstreamType,
): Promise<{ id: number }> {
  return api.post<{ id: number }>("jobs", "add_drawing", {
    body: { drawing_set_id: drawingSetId, drawing_type: drawingType },
  });
}

/** POST /create_bc_task — the "Create task" button on a BC drawing card. BC
 *  drawings only; 400 if a draw task already exists. */
export function createBcTask(drawingId: number): Promise<unknown> {
  return api.post<unknown>("jobs", "create_bc_task", { body: { drawing_id: drawingId } });
}

/** POST /set_drawing_status — the non-board path (set tab). Completing a
 *  planning/BC drawing closes its draw task and spawns the submit task. */
export function setDrawingStatus(drawingId: number, status: DrawingStatus): Promise<unknown> {
  return api.post<unknown>("jobs", "set_drawing_status", {
    body: { drawing_id: drawingId, status },
  });
}

/** POST /complete_drawing_set — force-finishes remaining drawings + closes their
 *  draw tasks WITHOUT spawning submit tasks (unblocks the next set). */
export function completeDrawingSet(drawingSetId: number): Promise<CompleteDrawingSetResponse> {
  return api.post<CompleteDrawingSetResponse>("jobs", "complete_drawing_set", {
    body: { drawing_set_id: drawingSetId },
  });
}

/** POST /update_drawing — the edit drawer. ALWAYS send the current title (it's
 *  written every call); send drawing_type only when changing it. */
export function updateDrawing(drawingId: number, input: DrawingUpdateInput): Promise<unknown> {
  return api.post<unknown>("jobs", "update_drawing", {
    body: { drawing_id: drawingId, title: input.title ?? "", drawing_type: input.drawing_type },
  });
}

/** POST /set_constraints — persist the site's planning constraints (a
 *  ConstraintsReport from the engine's POST /constraints) onto a job. Drives the
 *  Job Detail "Check planning constraints" action: look up via
 *  {@link feasibilityApi.lookupConstraints}, store here, re-fetch /detail. Returns
 *  the stored report. */
export function setJobConstraints(
  jobId: number,
  constraints: ConstraintsReport,
): Promise<{ constraints: ConstraintsReport }> {
  return api.post<{ constraints: ConstraintsReport }>("jobs", "set_constraints", {
    body: { job_id: jobId, constraints },
  });
}

/** GET /order_book — the cross-job Order book feed: every LIVE job with
 *  uninvoiced fees, each carrying its uninvoiced fee lines. `total` uses the
 *  identical calc as the overview dashboard order-book KPI (so the page total
 *  matches it exactly). No params; read-only. Fees are deleted via
 *  {@link feesApi.deleteFee}. */
export function getOrderBook(): Promise<OrderBookResponse> {
  return api.get<OrderBookResponse>("jobs", "order_book");
}
